/* ─── BEATS ────────────────────────────────────────────────────────────────
   The story's clock, and the one place the section and the robot arm agree
   on it.

   THE STORY, in one line: one board, carried into four sectors in turn, and
   every sector's services rise through the frame in front of it.

   Per sector the beats are always the same, and they alternate between the
   two performers:

     APPROACH   the arm carries the board in. The bay — the landing pad the
                board is set into — wakes up and the sector rises behind it.
     SEAT       CONTACT. The board is set down. The sector's core boots out
                of the thing it is standing on.
     CHARGE     power leaves the board along the feed and reaches the site.
     RISE       the services fly up through the frame, one at a time.
     HOLD       the finished sector sits still and is read.
     LIFT       the arm closes on the board again; everything retracts and the
                sector flies past the camera.

   Everything is measured in vh, and in ABSOLUTE vh rather than as fractions
   of a chapter — because the two performers have to meet. A fraction of a
   span means "however long that sector happens to be", and a six-service
   sector is 60vh longer than a four-service one; the arm's reach down onto
   the board takes exactly as long either way. Absolute beats are the only
   ones both sides can honour.

   The numbers below are the whole pacing of the section. Raise `perService`
   and every sector gets more room without a single other value moving.
   ───────────────────────────────────────────────────────────────────────── */

export const BEATS = {
  /* The first sector is the only one the arm has to fly IN to. It comes off
     the board's own flight across the page, has to descend, close on the
     board and carry it down — so it gets a full viewport of approach where
     the others only need the carry across from the sector before them. */
  approachFirst: 100,
  approach: 46,
  seat: 14,          // contact, and the core booting out of the board
  charge: 26,        // core takes power, the feed reaches the site
  perService: 30,    // one service's rise, hold and departure
  hold: 26,          // the finished picture, held
  lift: 34,          // jaws close, everything retracts, sector flies out
  /* The last sector never lifts — the finished picture is where the section
     should leave the reader — so it gets a plain tail instead. */
  tail: 40,
};

/* How long the incoming sector takes to rise into frame, and how far into
   the outgoing sector's exit it starts doing it. The overlap is what makes
   the hand-off read as flying THROUGH one sector into the next rather than
   as a cross-fade between two slides. */
export const RISE = 54;
export const RISE_LEAD = 16;

/* Where on the sector core the board is set down, as a fraction of the core
   radius below its centre. This is PcbJourney's `DROP_AIM` and it must stay
   equal to it: the arm aims at this point in world metres, the bay is drawn
   at this point in stage pixels, and if the two disagree the board lands
   next to its own landing pad. */
export const DROP_AIM = 0.62;

/* ── the schedule ─────────────────────────────────────────────────────────
   Turns the chapter list into absolute vh marks. Everything downstream — the
   section's height, the GSAP timeline, the arm's hand-over windows — is read
   off this one object, so none of them can drift out of step with another. */
export function buildSchedule(chapters) {
  const marks = chapters.map((ch, c) => {
    const isLast = c === chapters.length - 1;
    const approach = c === 0 ? BEATS.approachFirst : BEATS.approach;
    const deliverDur = BEATS.perService * Math.max(1, ch.services.length);
    const liftDur = isLast ? BEATS.tail : BEATS.lift;

    const seatAt = approach;
    const chargeAt = seatAt + BEATS.seat;
    const deliverAt = chargeAt + BEATS.charge;
    const holdAt = deliverAt + deliverDur;
    const liftAt = holdAt + BEATS.hold;

    return {
      isLast,
      seatAt, chargeAt, deliverAt, deliverDur, holdAt, liftAt, liftDur,
      span: liftAt + liftDur,
    };
  });

  const starts = [];
  let acc = 0;
  for (const m of marks) { starts.push(acc); acc += m.span; }
  return { marks, starts, total: acc };
}

/* ── the hand-over contract ───────────────────────────────────────────────
   The section owns the clock; the arm is a consumer of it. This is the whole
   of what crosses between them, and it crosses in ONE direction.

   It crosses as SCROLL PIXELS, read off the story's own live ScrollTrigger,
   rather than as fractions the other side has to convert.

   Both work today: measured on the page, deriving the range by hand as
   `top + (height - viewport)` lands within a pixel of what the trigger
   actually resolves. The reason to publish pixels anyway is that the
   hand-rolled version is a SECOND implementation of ScrollTrigger's scroll
   mapping, and it only stays correct while nothing about the trigger changes.
   Add `pinSpacing`, move the start, pin something above this section — and one
   of the two implementations silently becomes wrong, with the symptom being
   the jaws opening somewhere other than where the bay closes. There is one
   mapping from scroll to story time on this page; the trigger owns it, and
   nobody else gets a vote.

   Republished on every refresh, which is also every resize, so nothing goes
   stale.

   `active` is false whenever the desktop story is not on screen (mobile, or
   the section unmounted), and PcbJourney falls back to the single delivery it
   did before this existed. The arm is never left waiting for a cue that is
   not coming. */
export const ARM_SYNC = {
  active: false,
  cycles: [],     // [{ seatY, liftY }] one per sector, in absolute scroll px
  version: 0,     // bumped on every change, so consumers can cheaply re-read
};

/* Called from the story's own ScrollTrigger, with the trigger's resolved
   `start` and `end` — the only numbers on the page that know where this
   section really begins and ends in scroll. */
export function publishArmSync({ marks, starts, total }, start, end) {
  const span = Math.max(1, end - start);
  ARM_SYNC.cycles = marks.map((m, c) => ({
    seatY: start + ((starts[c] + m.seatAt) / total) * span,
    liftY: start + ((starts[c] + m.liftAt) / total) * span,
  }));
  ARM_SYNC.active = true;
  ARM_SYNC.version++;
}

export function clearArmSync() {
  ARM_SYNC.active = false;
  ARM_SYNC.cycles = [];
  ARM_SYNC.version++;
}
